<template>
    <div class="definition-preview">
        <ProcessDefinition
            v-if="definition?.content"
            v-model:bpmnArtefact="definition"
            v-model:highlightElementId="highlightElementId"
            :canEdit="false"
            :isNavigationEnabled="true"
            :isScrollingEnabled="false"
            :isMaximizingEnabled="true"
            :isDisabled="false"
            :isHoveringEnabled="false"
            :isZoomToHighlightEnabled="true"
        />
    </div>

    <TaskPage v-if="entityType === EntityType.TASK" />
    <GatewayPage v-if="entityType === EntityType.GATEWAY" />
    <FlowPage v-if="entityType === EntityType.FLOW" />
    <EventPage v-if="entityType === EntityType.EVENT" />
</template>

<script setup lang="ts">
import ProcessDefinition from "@/components/Process/Definition.vue";
import { useProcessManager } from "@/composables/process-manager";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useProcessElementsManager } from "@/stores/process-elements-store";
import { computed, nextTick, ref, watch } from "vue";
import EventPage from "./EventPage.vue";
import FlowPage from "./FlowPage.vue";
import GatewayPage from "./GatewayPage.vue";
import TaskPage from "./TaskPage.vue";

const { processElements } = useProcessElementsManager();

const { identity, entityType } = useCurrentEntityManager();
const { definition } = useProcessManager();

const highlightElementId = ref<string | null>();

const currentProcessElement = computed(() => {
    return processElements.value?.find(
        (element) => element.identity === identity.value
    );
});

const highlightCurrentElement = () => {
    // Reset to null first to force the watch to trigger even with the same ID
    highlightElementId.value = null;
    // Wait for the next tick to ensure the reset is processed
    nextTick(() => {
        highlightElementId.value = currentProcessElement.value?.bpmn_id || null;
    });
};

// Watch for changes in the process element
watch(
    currentProcessElement,
    () => {
        highlightCurrentElement();
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.definition-preview {
    // Negative margin of the page padding so that it
    // fills the whole page instead.
    margin: calc(var(--page-margin) * -0.5) calc(var(--page-margin) * -1);
    margin-bottom: var(--default-gap);
    align-items: center;
    height: 25vh;
    background-color: var(--surface-background-mild);
}
</style>
